﻿import { useState, useEffect, useRef, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { GiCarDoor } from "react-icons/gi";
import { IoLocationOutline, IoSnowOutline } from "react-icons/io5";
import PhoneInput from "react-phone-input-2";
import { CountryDropdown, RegionDropdown } from "react-country-region-selector";
import axios from "axios";
import { ArrowLeft } from "lucide-react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import {
  FaCar,
  FaExclamationTriangle,
  FaSpinner,
  FaUser,
  FaMapMarkerAlt,
  FaShieldAlt,
  FaInfoCircle,
  FaChevronDown,
  FaChevronUp,
  FaUserFriends,
  FaSuitcaseRolling,
  FaEye, // <--- ADDED
  FaEyeSlash, // <--- ADDED
} from "react-icons/fa";
import { Calendar as CalendarIcon } from "lucide-react";
import aombre from "../images/logo-dark.png";
import logo from "../images/Aombre-round-logo.png";
import "react-phone-input-2/lib/style.css";

function Checkout() {
  const location = useLocation();
  const navigate = useNavigate();

  // Extract data from navigation state
  const { vehicleData, searchData } = location.state || {};
  // console.log(`vehicle-datah: ${JSON.stringify(vehicleData)}`);
  // console.log(`search-datah: ${JSON.stringify(searchData)}`);
  // console.log("Selected Vehicle Payment Type:", vehicleData?.paymentType);

  // State for API data
  const [optionalServices, setOptionalServices] = useState([]);
  const [policies, setPolicies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  const [showPolicies, setShowPolicies] = useState(false);
  const [showTaxes, setShowTaxes] = useState(false);
  const [showExtras, setShowExtras] = useState(false);
  const [showPopup, setShowPopup] = useState(false);
  const [showPopupFailedTransaction, setPopupFailedTransaction] = useState(false);
  const [error, setError] = useState(null);

  // Payment type detection and card details state
  const [paymentType, setPaymentType] = useState(null); // 'POA' or 'PP'

  // --- ADDED: State for Password Toggle ---
  const [showCardNumber, setShowCardNumber] = useState(false);
  const [showCVV, setShowCVV] = useState(false);

  // Session timeout state
  const timeoutRef = useRef(null);
  const [sessionExpired, setSessionExpired] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Session timeout logic
  useEffect(() => {
    const resetTimeout = () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      timeoutRef.current = setTimeout(() => {
        setSessionExpired(true);
      }, 120 * 1000); // 120 seconds
    };

    const handleActivity = () => {
      resetTimeout();
    };

    // Initial timeout
    resetTimeout();

    // Add event listeners for user activity
    window.addEventListener("mousemove", handleActivity);
    window.addEventListener("keydown", handleActivity);
    window.addEventListener("click", handleActivity);
    window.addEventListener("scroll", handleActivity);

    // Cleanup
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      window.removeEventListener("mousemove", handleActivity);
      window.removeEventListener("keydown", handleActivity);
      window.removeEventListener("click", handleActivity);
      window.removeEventListener("scroll", handleActivity);
    };
  }, []);

  // console.log("vehicleData",vehicleData);
  // Normalize numbers
  const baseRate = Number(vehicleData?.Base_Price_rate_table || 0);
  const taxOnCar = Number(vehicleData?.Tax_on_Car_Price_Value || 0);
  const carPrice = Number(vehicleData?.Car_Price || baseRate);

  // Form state
  function getTaxBreakdown(taxData) {
    const calculated = taxData?.calculated_tax_charge || {};
    const independent = taxData?.independent_tax_charge || {};

    const allTaxes = {
      ...calculated,
      ...independent,
    };

    return Object.entries(allTaxes)
      .map(([key, value]) => `${key} : ${value}`)
      .join(", ");
  }

  // Usage:
  const breakdown = getTaxBreakdown(
    vehicleData?.Tax_on_Car_Price_Details_Breakup
  );
  // Output: HST : 43.72, Prefered Location Surcharge(PLS) : 48.7
  useEffect(() => {
    const script = document.createElement("script");
    script.src = "https://checkout.stripe.com/checkout.js";
    script.async = true;
    document.body.appendChild(script);

    script.onload = () => {
      window.StripeCheckout.configure({
        key: "sk_test_gihvqEEKT7fktQw0MDglIRLv00UpxLNjwN", //test key
        locale: "auto",
      });
    };

    return () => {
      document.body.removeChild(script);
    };
  }, []);

  const [captchaText, setCaptchaText] = useState("");
  const [userCaptchaInput, setUserCaptchaInput] = useState("");

  const generateCaptcha = () => {
    const characters =
      "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
    let captcha = "";
    for (let i = 0; i < 6; i++) {
      captcha += characters.charAt(
        Math.floor(Math.random() * characters.length)
      );
    }
    setCaptchaText(captcha);
  };

  useEffect(() => {
    generateCaptcha();
  }, []);

  const [formData, setFormData] = useState({
    // user info
    First_Name: "",
    Last_Name: "",
    Email_ID: "",
    Phone_No: "",
    DOB: "",
    Age: "",
    Address: "",
    city: "",
    state: "",
    country: "",
    zip_code: "",
    driver_country: "",

    // Flight info
    flight_number: "",
    flight: "",

    // Card details (for POA)
    c_name: "",
    c_number: "",
    c_expiry_m: "",
    c_expiry_y: "",
    c_code: "",

    // T & C
    accept_terms: false,

    // Selected services
    selectedServices: [],

    // Additional calculated fields
    Base_Price_rate_table: baseRate,
    Total_Price: baseRate + taxOnCar,
    // FINAL PRICE
    Car_Price: carPrice,
    Tax_on_Car_Price_Value: taxOnCar,
    Extras_Total_Price: 0,
    Extras_Base_Value: 0,
    Tax_on_Extras: 0,
    Tax_on_Extras_Details: "",
    Tax_on_Car_Price_Details_Breakup: breakdown,
  });

  //  optional services
  // Add this at the top of Checkout.js
  function formatLocalDateTime(date) {
    if (!date) return "";
    const d = new Date(date);
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    const hours = String(d.getHours()).padStart(2, "0");
    const minutes = String(d.getMinutes()).padStart(2, "0");

    return `${year}-${month}-${day}T${hours}:${minutes}`;
  }

  // optional services

  const fetchOptionalServices = async () => {
    try {
      const baseUrl = process.env.REACT_APP_API_BASE_URL;
      // Guard clause to ensure data is available
      if (!vehicleData || !searchData) {
        console.log("Waiting for vehicleData and searchData...");
        return;
      }

      const payload = {
        location_id: `690337-${searchData.pickup.pickup_airport_code}`,
        drop_location_id: `690337-${searchData.dropoff.dropoff_airport_code}`,
        country: searchData.pickup.pickup_country,
        car_class: vehicleData.SIPP_Code || vehicleData.vehicleSubType || "",
        pickup: `${searchData.pickup.pickup_date}:00.000`,
        dropoff: `${searchData.dropoff.dropoff_date}:00.000`,
        pickup_time: searchData.pickup.pickup_date
          ? formatLocalDateTime(searchData.pickup.pickup_date).split("T")[1]
          : "",
        dropoff_time: searchData.dropoff.dropoff_date
          ? formatLocalDateTime(searchData.dropoff.dropoff_date).split("T")[1]
          : "",
      };

      console.log("Sending payload to get_optional_services:", payload);
 
      const response = await axios.post(
      `${baseUrl}get_optional_services`,
        payload
      );

      const servicesWithUid = (response.data || []).map((s, index) => ({
        ...s,
        uid: index + 1,
      }));

      setOptionalServices(servicesWithUid);
    } catch (err) {
      console.error("Error fetching optional services:", err);
      setOptionalServices([]);
    }
  };

  //  policies
  const fetchPolicies = async () => {
    try {
      const baseUrl = process.env.REACT_APP_API_BASE_URL;
      const locationCode = searchData?.pickup?.pickup_airport_code || "YYZ";
      const response = await axios.get(
        `${baseUrl}new_routes_website_api_all_policies/${locationCode}`,
        { timeout: 10000 }
      );
      setPolicies(response.data || []);
    } catch (err) {
      console.error("Error fetching policies:", err);
      setPolicies([]);
    }
  };

  useEffect(() => {
    if (vehicleData && searchData) {
      setFormData((prev) => ({
        ...prev,
        driver_country: searchData?.pickup?.pickup_country || "",
        pickup_country: searchData?.pickup?.pickup_country || "",
        country: searchData?.pickup?.pickup_country || "",
      }));

      // Detect payment type from vehicleData
      const detectPaymentType = () => {
        if (!vehicleData.paymentType) return;

        if (typeof vehicleData.paymentType === "string") {
          setPaymentType(vehicleData.paymentType);
        } else {
          const paymentKeys = Object.keys(vehicleData.paymentType);
          let selectedType = paymentKeys[0];
          if (paymentKeys.includes("POA")) {
            selectedType = "POA";
          } else if (paymentKeys.includes("PP")) {
            selectedType = "PP";
          }
          setPaymentType(selectedType);
        }
      };
      detectPaymentType();

      Promise.all([fetchOptionalServices(), fetchPolicies()]).finally(() =>
        setLoading(false)
      );
    } else {
      setLoading(false);
      setError("Missing booking data. Please start over.");
    }
  }, [vehicleData, searchData]);

  // Handle optional services selection
  const handleServiceSelection = (service) => {
    setFormData((prev) => {
      const isSelected = prev.selectedServices.some(
        (s) => s.uid === service.uid
      );
      let newSelectedServices;

      if (isSelected) {
        newSelectedServices = prev.selectedServices.filter(
          (s) => s.uid !== service.uid
        );
      } else {
        newSelectedServices = [...prev.selectedServices, service];
      }

      const extrasTotal = newSelectedServices.reduce(
        (sum, s) => sum + Number(s.total || 0),
        0
      );

      const taxOnExtras = 0;
      const taxOnExtrasDetails =
        newSelectedServices.length > 0
          ? newSelectedServices.map((s) => `${s.name} : ${s.total}`).join(", ")
          : "";

      return {
        ...prev,
        selectedServices: newSelectedServices,
        Extras_Total_Price: extrasTotal,
        Extras_Base_Value: extrasTotal,
        Tax_on_Extras: taxOnExtras,
        Tax_on_Extras_Details: taxOnExtrasDetails,
        car_total:
          Number(prev.Base_Price_rate_table || 0) +
          Number(prev.Tax_on_Car_Price_Value || 0),
        Total_Price:
          Number(prev.Base_Price_rate_table || 0) +
          Number(prev.Tax_on_Car_Price_Value || 0) +
          Number(extrasTotal || 0) +
          Number(taxOnExtras || 0),
      };
    });
  };

  // Handle input change
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    let sanitizedValue = value;

    // Restrict card number to digits and spaces only
    if (name === "c_number") {
      sanitizedValue = value.replace(/[^0-9\s]/g, "");
    }
    // Restrict CVV to digits only
    else if (name === "c_code") {
      sanitizedValue = value.replace(/[^0-9]/g, "");
    }

    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : sanitizedValue,
    }));
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  // Calculate Age
  const calculateAge = (dob) => {
    if (!dob) return "";
    const birthDate = new Date(dob);
    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();

    if (
      monthDiff < 0 ||
      (monthDiff === 0 && today.getDate() < birthDate.getDate())
    ) {
      age--;
    }

    return age.toString();
  };

  const handleDobChange = (e) => {
    const dob = e.target.value;
    const age = calculateAge(dob);

    setFormData((prev) => ({
      ...prev,
      DOB: dob,
      Age: age,
    }));
  };

  // OPTIONAL SERVICES FOR AGE CHECKBOX

  // Auto-check Underage Fee if age between 21-24
  useEffect(() => {
    if (!optionalServices || optionalServices.length === 0) return;

    const age = parseInt(formData.Age, 10);
    const underageService = optionalServices.find(
      (s) =>
        s.name.toLowerCase().includes("under age") ||
        s.name.toLowerCase().includes("driver's age")
    );

    if (!underageService) return;

    setFormData((prev) => {
      let newSelectedServices = [...prev.selectedServices];

      if (age >= 21 && age <= 24) {
        // Add underage fee if not already added
        if (!newSelectedServices.some((s) => s.uid === underageService.uid)) {
          newSelectedServices.push(underageService);
        }
      } else {
        // Remove underage fee if age is outside 21-24
        newSelectedServices = newSelectedServices.filter(
          (s) => s.uid !== underageService.uid
        );
      }

      const extrasTotal = newSelectedServices.reduce(
        (sum, s) => sum + Number(s.total || 0),
        0
      );

      return {
        ...prev,
        selectedServices: newSelectedServices,
        Extras_Total_Price: extrasTotal,
        Extras_Base_Value: extrasTotal,
        Total_Price:
          Number(prev.Base_Price_rate_table || 0) +
          Number(prev.Tax_on_Car_Price_Value || 0) +
          Number(extrasTotal || 0),
      };
    });
  }, [formData.Age, optionalServices]);

  // Validate form
  const validateForm = () => {
    const errors = {};
    const requiredFields = ["First_Name", "Last_Name", "Email_ID", "Phone_No"];

    // 1️⃣ Basic required fields
    requiredFields.forEach((field) => {
      if (!formData[field]) {
        errors[field] = `${field.replace(/_/g, " ")} is required`;
      }
    });

    // Date of Birth check
    if (!formData.Age) {
      errors.DOB = "Date of Birth is required";
    }

    // 2️⃣ Email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (formData.Email_ID && !emailRegex.test(formData.Email_ID)) {
      errors.Email_ID = "Invalid email format";
    }

    // 3️⃣ Phone format
    const phoneRegex = /^[0-9+\-\s()]{7,15}$/;
    if (formData.Phone_No && !phoneRegex.test(formData.Phone_No)) {
      errors.Phone_No = "Invalid phone number format";
    }

    // 4️⃣ Age check
    if (!formData.Age) {
      errors.DOB = "Age is required";
    } else if (parseInt(formData.Age, 10) < 21) {
      errors.DOB = "Driver must be at least 21 years old";
    }

    // 5️⃣ CAPTCHA
    if (!userCaptchaInput) {
      errors.captcha = "CAPTCHA is required";
    } else if (userCaptchaInput !== captchaText) {
      errors.captcha = "CAPTCHA does not match";
      generateCaptcha();
      setUserCaptchaInput("");
    }

    // 6️⃣ Terms
    if (!formData.accept_terms) {
      errors.accept_terms = "You must accept the terms and conditions";
    }

    // 7️⃣ Payment Info validation (for POA)
    if (paymentType === "POA") {
      const { c_name, c_number, c_expiry_m, c_expiry_y, c_code } = formData;

      if (!c_name.trim()) errors.c_name = "Cardholder name is required";

      // Card number (basic Luhn + length check)
      const cleanCardNumber = c_number.replace(/\s+/g, "");
      const cardNumberRegex = /^\d{12,16}$/;
      if (!cleanCardNumber || !cardNumberRegex.test(cleanCardNumber)) {
        errors.c_number = "Card number must be 12-16 digits";
      }

      // Expiry month/year
      if (!c_expiry_m) errors.c_expiry_m = "Expiry month is required";
      if (!c_expiry_y) errors.c_expiry_y = "Expiry year is required";

      // Check if expiry is in the future
      if (c_expiry_m && c_expiry_y) {
        const now = new Date();
        const expDate = new Date(
          parseInt(c_expiry_y, 10),
          parseInt(c_expiry_m, 10) - 1
        );
        if (expDate < now) {
          errors.c_expiry_m = "Card has expired";
        }
      }

      // CVV check (3–4 digits)
      const cvvRegex = /^\d{3,4}$/;
      if (!c_code || !cvvRegex.test(c_code)) {
        errors.c_code = "Invalid CVV code";
      }
    }

    return errors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate form including CAPTCHA
    const validationErrors = validateForm();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setErrors({});
    setSubmitting(true);
    setError(null);

    try {
      //

      // Prepare booking payload
      const payload = {
        OTA: "Aombre Website",
        Booked_ip: "",
        DOB: formData.DOB,
        Age: formData.Age,
        Address: formData.Address,
        Rate_Code: paymentType === "POA" ? "CNI" : "CPNI",
        Pickup_Location: searchData?.pickup?.pickup_airport_code,
        DropOff_Location: searchData?.dropoff?.dropoff_airport_code,
        Pickup_Date_Time: `${searchData.pickup.pickup_date}:00.000`,
        DropOff_Date_Time: `${searchData.dropoff.dropoff_date}:00.000`,
        Booking_Date_Time: new Date().toISOString(),
        SIPP_Code: vehicleData.SIPP_Code,
        First_Name: formData.First_Name,
        Last_Name: formData.Last_Name,
        Email_ID: formData.Email_ID,
        Phone_No: formData.Phone_No,
        city: formData.city,
        state: formData.state,
        country: formData.country,
        zip_code: formData.zip_code,
        flight_number: formData.flight_number || "",
        flight: formData.flight || "",
        uniqe_id: "",
        pay_type: paymentType,
        accept_terms: formData.accept_terms ? "1" : "0",
        Base_Price_rate_table: Number(formData.Base_Price_rate_table || 0),
        Total_Price: parseFloat((formData.Total_Price || 0).toFixed(2)),
        Car_Price: Number(formData.Car_Price || 0),
        Tax_on_Car_Price_Value: Number(
          Number(formData.Tax_on_Car_Price_Value || 0).toFixed(2)
        ),

        Tax_on_Car_Price_Details_Breakup:
          formData.Tax_on_Car_Price_Details_Breakup,
        Extras_Total_Price: Number(formData.Extras_Total_Price || 0),
        Extras_Base_Value: Number(formData.Extras_Base_Value || 0),
        Tax_on_Extras: Number(formData.Tax_on_Extras || 0),
        Currency: vehicleData?.Currency,
        // Tax_on_Extras_Details: formData.Tax_on_Extras_Details,
        Tax_on_Extras_Details: formData.Tax_on_Extras_Details
          ? formData.Tax_on_Extras_Details.replace(
              /: (\d+\.?\d*)/g,
              (m, n) => ": " + parseFloat(n).toFixed(2)
            )
          : "",
        upload_to_TSD: 0,
        upload_to_rent_centric: 0,
        upload_to_mexico_file: 2,
        Account_ID: "690337",
        length_of_rental: vehicleData?.length_of_rental,
        Status_Valid_Cancelled_No_Show: "Valid",
        driver_country: formData.driver_country,
        pickup_country: searchData?.pickup?.pickup_country,
        Promocode: "",
        skyscanner_redirectid: null,
        booking_from: "en",
        c_name: formData.c_name || "",
        c_number: formData.c_number || "",
        c_expiry_m: formData.c_expiry_m || "",
        c_expiry_y: formData.c_expiry_y || "",
        c_code: formData.c_code || "",
        token: "",

        ...(vehicleData?.taxes_detail?.calculated_tax_charge || {}),
        ...(vehicleData?.taxes_detail?.baserate_tax_charge || {}),
        ...(vehicleData?.taxes_detail?.independent_tax_charge || {}),
        ...(vehicleData?.taxes_detail?.dropoff_fee_charge || {}),

        total_taxes: vehicleData?.taxes_detail?.total_taxes || 0
      };

      if (paymentType === "PP") {
        const baseUrl = process.env.REACT_APP_API_BASE_URL;
        const response1 = await axios.post(`${baseUrl}/fiserv/credentials`,
          payload,
          { headers: { "Content-Type": "application/json" } }
        );
        const accessToken = response1.data.result.accessToken; // from backend
        const publicKey = response1.data.result.publicKey; // from backend
        const keyId = response1.data.result.keyId; // from backend
        document.getElementById("fiserv-container").innerHTML = "";
        document.getElementById("fiserv-popup-overlay").style.display = "flex";
        // ✅ Dynamically load Fiserv SDK
        const script = document.createElement("script");
        script.src = "https://commercehub-secure-data-capture.fiservapps.com/3.2.7/checkout.js";
        script.async = true;

        script.onload = async () => {
          console.log("✅ Fiserv SDK Loaded");

          if (!window.fiserv || !window.fiserv.components) {
            console.error("❌ Fiserv SDK not available after load");
            return;
          }

          try {
            await window.fiserv.components.hostedCheckout({
              credentials: {
                environment: "CERT",
                apiKey: "iKYaPAHMIkuhD8iOr5poqtwww2xjVmZ9rkRAUMsqYiKQTA0v",
                accessToken: accessToken, // use the token from backend
                merchantId: "100495000000050",
                terminalId: "10000001",
                pageId: "535256a2-303e-4ec8-a5a3-ff9116e68045",
                pageVersion: "1"
              },
              integrationOptions: {
                type: "FRAME",
                parentElementId: "fiserv-container",
              }
            })
            .then(async (data) => {
                if(data.isSuccessful == true){
                  document.getElementById("fiserv-popup-overlay").style.display = "none";
                  const response2 = await axios.post(`${baseUrl}/fiserv/charges`,
                    {"credentialsData":response1.data,"extraData":payload},
                    { headers: { "Content-Type": "application/json" } }
                  );
                  const data2 = response2.data;
                  if (data2.status === "success") {
                    const updatedPayload = {
                      ...payload,
                      orderId: data2.orderId   // coming from backend response
                    };
                    const response = await axios.post(` ${baseUrl}/new_routes_website_api_add_reservation`,
                      updatedPayload,
                      { headers: { "Content-Type": "application/json" } }
                    );

                    if (response.data && response.data.success === "1") {
                      const bookingNum =
                        response.data.rez_number ||
                        response.data.booking_number ||
                        "N/A";

                      // Navigate to confirmation page with booking details
                      navigate("/confirmation", {
                        state: {
                          bookingNumber: bookingNum,
                          vehicleData,
                          searchData,
                          formData: {
                            ...formData,
                            selectedServices: formData.selectedServices || [],
                          },
                        },
                      });
                    }else if(response.data && response.data.success == "0" && response.data.success_text == "Duplicate reservation"){
                      setShowPopup(true);
                      setSubmitting(false);
                    } else {
                      throw new Error(
                        response.data?.error ||
                          response.data?.message ||
                          "Booking failed"
                      );
                    }
                  }else{
                    setPopupFailedTransaction(true)
                  }
                }else{
                  document.getElementById("fiserv-popup-overlay").style.display = "none";
                  setPopupFailedTransaction(true)
                }
                  // trigger api call
            })
            .catch((error) => {
                console.error('Error:', JSON.stringify(error));
            });
          } catch (err) {
            console.error("❌ Hosted Checkout Error:", err);
          }
        };

        script.onerror = () => {
          console.error("❌ Failed to load Fiserv SDK");
        };

        document.body.appendChild(script);
        
      } else if (paymentType === "POA") {
        // For POA, submit directly without Stripe
        const baseUrl = process.env.REACT_APP_API_BASE_URL;
        try {
          const response = await axios.post(
            `${baseUrl}new_routes_website_api_add_reservation`,
            payload,
            { headers: { "Content-Type": "application/json" } }
          );

          if (response.data && response.data.success === "1") {
            const bookingNum =
              response.data.rez_number || response.data.booking_number || "N/A";

            // Navigate to confirmation page with booking details
            navigate("/confirmation", {
              state: {
                bookingNumber: bookingNum,
                vehicleData,
                searchData,
                formData: {
                  ...formData,
                  selectedServices: formData.selectedServices || [],
                },
              },
            });
          } else {
            throw new Error(
              response.data?.error || response.data?.message || "Booking failed"
            );
          }
        } catch (err) {
          setError("Booking failed. Please try again.");
          setSubmitting(false);
        }
      }
    } catch (err) {
      setError("An unexpected error occurred. Please try again.");
      setSubmitting(false);
    }
  };

  //

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <img
            src={logo}
            alt="Aombre Logo"
            className="w-48 h-48 mx-auto  mb-4 animate-spin"
          />
          <p className="text-lg text-gray-600">
            Loading booking information...
          </p>
        </div>
      </div>
    );
  }

  if (!vehicleData || !searchData) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <FaExclamationTriangle className="text-4xl text-red-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-800 mb-2">
            Missing Booking Data
          </h2>
          <p className="text-gray-600 mb-4">
            Please start your booking process again.
          </p>
          <button
            onClick={() => navigate("/")}
            className="bg-[#ca8032] text-white px-6 py-2 rounded-lg hover:bg-orange-700"
          >
            Start Over
          </button>
        </div>
      </div>
    );
  }

  if (sessionExpired) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-800 mb-4">
            Session Expired
          </h2>
          <p className="text-gray-600 mb-4">
            Your session has expired due to inactivity.
          </p>
          <button
            onClick={() => navigate("/")}
            className="bg-[#ca8032] text-white px-6 py-2 rounded-lg hover:bg-orange-700"
          >
            Back to Home
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      <div id="fiserv-popup-overlay" style={{ display: "none" }}>
        <div id="fiserv-container"></div>
      </div>
      {showPopup && (
        <div className="fixed inset-0 flex items-center justify-center bg-black/50 z-50">
          <div className="bg-white rounded-lg shadow-lg p-6 w-80 text-center">
            <h2 className="text-lg font-semibold text-red-600 mb-2">
              Duplicate Reservation
            </h2>
            <p className="text-gray-600 mb-4">
              It looks like you already have a booking for these dates. Please
              check your reservations before continuing.
            </p>
            <button
              onClick={() => setShowPopup(false)}
              className="px-4 py-2 bg-[#ca8032] text-white rounded-md hover:bg-[#b36e29] transition"
            >
              Close
            </button>
          </div>
        </div>
      )}
      {showPopupFailedTransaction && (
        <div className="fixed inset-0 flex items-center justify-center bg-black/50 z-50">
          <div className="bg-white rounded-lg shadow-lg p-6 w-80 text-center">
            <h2 className="text-lg font-semibold text-red-600 mb-2">
              Transaction Declined  
            </h2>
            <p className="text-gray-600 mb-4">
              The card validation could not be completed at this time. No amount has been charged.
              Please verify your card details and try again.
            </p>
            <button
              onClick={() => setPopupFailedTransaction(false)}
              className="px-4 py-2 bg-[#ca8032] text-white rounded-md hover:bg-[#b36e29] transition"
            >
              Close
            </button>
          </div>
        </div>
      )}

      <div className="min-h-screen py-8 bg-[#edebec] mt-[66px]">
        <div className="max-w-6xl mx-auto px-4">
          <button
            onClick={() => navigate(-1)}
            className=" mt-[60px] hidden md:flex absolute top-6 left-6 items-center text-gray-700 hover:text-[#ca8032] transition"
          >
            <ArrowLeft className="w-6 h-6 mr-1" />
            Back
          </button>
          <div id="card-form">
            <div id="card-number"></div>
            <div id="name-on-card"></div>
            <div id="expiration"></div>
            <div id="cvv"></div>
          </div>

          <h1 className="text-3xl font-bold text-gray-800 mb-8 text-center">
            Complete Your <span className="text-[#ca8032]">Booking</span>
          </h1>

          {error && (
            <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-6">
              <FaExclamationTriangle className="inline mr-2" />
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit}>
            <div className="flex flex-col lg:flex-row gap-8">
              {/* LEFT COLUMN */}
              <div className="lg:w-2/3 ">
                {/* Booking Summary */}
                <div className="bg-white rounded-lg shadow-lg ">
                  <div className="bg-[#006039] text-[#edebec] p-4 ">
                    <h2 className="text-xl font-semibold flex items-center">
                      <FaCar className="mr-2 text-[#edebec]" />
                      Booking Summary
                    </h2>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 p-6  mb-4">
                    <div>
                      {vehicleData?.Vehicle_Image && (
                        <img
                          src={vehicleData.Vehicle_Image}
                          alt={vehicleData.Vehicle_Name || "Vehicle"}
                          className="w-full rounded-md mb-2 max-h-40 object-contain"
                        />
                      )}
                      <div className="flex items-center space-x-2 mt-4">
                        <p className="text-sm text-gray-900">
                          Service Provided by:
                        </p>
                        <img
                          src={aombre}
                          className="h-6 object-contain"
                          alt="Aombre Logo"
                        />
                      </div>
                    </div>

                    <div className="space-y-4">
                      <div>
                        <div className="flex items-center gap-1 mb-3">
                          <h3 className="text-lg font-bold text-gray-800">
                            {vehicleData.Vehicle_Name}
                          </h3>
                          <p className="text-gray-600 text-sm">or similar</p>
                        </div>
                        <div className="flex flex-row justify-around">
                          <div className="flex items-center gap-1">
                            <FaUserFriends className="text-[#ca8032]" />
                            {vehicleData.Seats}
                          </div>
                          <div className="flex items-center gap-1">
                            <FaSuitcaseRolling className="text-[#ca8032]" />
                            {vehicleData.Bags}
                          </div>
                          <div className="flex items-center gap-1">
                            <IoSnowOutline className="text-[#ca8032]" />
                            {vehicleData.AC}
                          </div>
                          <div className="flex items-center gap-1">
                            <GiCarDoor className="text-[#ca8032]" />
                            {vehicleData.Doors}
                          </div>
                          {/* <div className="flex items-center gap-1">
                            <FaCogs className="text-[#ca8032]" />
                            {vehicleData.Transmission}
                          </div> */}
                        </div>
                      </div>

                      {/* Pickup / Dropoff */}
                      <div>
                        <div className="flex items-center gap-1 mb-2">
                          <IoLocationOutline />
                          <h3 className="font-medium text-gray-700">Pickup</h3>
                        </div>
                        <p className="text-gray-600">
                          {searchData.pickup.pickup_location}
                        </p>
                        <p className="text-sm text-gray-500">
                          {new Date(
                            searchData.pickup.pickup_date
                          ).toLocaleString()}
                        </p>
                      </div>

                      <div>
                        <div className="flex items-center gap-1">
                          <IoLocationOutline />
                          <h3 className="font-medium text-gray-700">Dropoff</h3>
                        </div>
                        <p className="text-gray-600">
                          {searchData.dropoff.dropoff_location}
                        </p>
                        <p className="text-sm text-gray-500">
                          {new Date(
                            searchData.dropoff.dropoff_date
                          ).toLocaleString()}
                        </p>
                        {/* here i need to chnage */}
                        <p className="text-[#ca8032] text-sm mt-2">
                          Rental Period: {vehicleData.length_of_rental}{" "}
                          {vehicleData.length_of_rental < 2 ? "day" : "days"}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Customer Info, Address, Flight, Optional Services, Policies */}
                <div className="bg-white rounded-lg shadow-lg p-4 md:p-6 mt-6">
                  {/* Customer Information */}
                  <div className="mb-6 md:mb-8">
                    <h2 className="text-xl font-semibold mb-3 md:mb-4 flex items-center">
                      <FaUser className="mr-2 text-[#ca8032]" />
                      Customer Information
                    </h2>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          First Name *
                        </label>
                        <input
                          type="text"
                          name="First_Name"
                          value={formData.First_Name}
                          onChange={handleInputChange}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                          required
                        />
                        {errors.First_Name && (
                          <p className="text-red-500 text-sm">
                            {errors.First_Name}
                          </p>
                        )}
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Last Name *
                        </label>
                        <input
                          type="text"
                          name="Last_Name"
                          value={formData.Last_Name}
                          onChange={handleInputChange}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                          required
                        />
                        {errors.Last_Name && (
                          <p className="text-red-500 text-sm">
                            {errors.Last_Name}
                          </p>
                        )}
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Email Address *
                        </label>
                        <input
                          type="email"
                          name="Email_ID"
                          value={formData.Email_ID}
                          onChange={handleInputChange}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                          required
                        />
                        {errors.Email_ID && (
                          <p className="text-red-500 text-sm">
                            {errors.Email_ID}
                          </p>
                        )}
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Phone Number *
                        </label>
                        <PhoneInput
                          containerClass="w-full"
                          country={"us"} // default country
                          value={formData.Phone_No}
                          onChange={(phone) => {
                            setFormData({ ...formData, Phone_No: phone });
                            setErrors((prev) => ({ ...prev, Phone_No: "" }));
                          }}
                          inputClass="!w-full !pl-14 !px-3 !py-2 !border !border-gray-300 !rounded-md focus:!outline-none focus:!ring-2 focus:!ring-orange-500"
                          buttonClass="!border-gray-300"
                          inputProps={{
                            name: "Phone_No",
                            required: true,
                          }}
                        />
                        {errors.Phone_No && (
                          <p className="text-red-500 text-sm">
                            {errors.Phone_No}
                          </p>
                        )}
                      </div>

                      {/* dob */}
                      <div className="w-full max-w-sm p-4">
                        <div className="flex flex-col">
                          <label
                            htmlFor="dob"
                            className="block text-sm font-medium text-gray-700 mb-1"
                          >
                            Date of Birth *
                          </label>

                          {/* Wrapper for the input and icon */}
                          <div className="relative w-full">
                            <DatePicker
                              autoComplete="off"
                              id="dob"
                              selected={
                                formData.DOB ? new Date(formData.DOB) : null
                              }
                              onChange={(date) => {
                                setFormData({
                                  ...formData,
                                  DOB: date,
                                  Age: calculateAge(date),
                                });
                                setErrors((prev) => ({ ...prev, DOB: "" }));
                              }}
                              // --- UI/UX Improvements ---
                              showMonthDropdown // Add month dropdown for faster navigation
                              showYearDropdown
                              scrollableYearDropdown
                              yearDropdownItemNumber={100}
                              maxDate={new Date()} // Prevent future dates
                              placeholderText="DD / MM / YYYY"
                              dateFormat="dd / MM / yyyy"
                              // --- Applying Custom Styles ---
                              calendarClassName="custom-datepicker" // Use our custom class for the calendar popup
                              wrapperClassName="w-full"
                              className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500" // Add left padding for the icon
                            />
                            {/* Calendar Icon */}
                            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                              <CalendarIcon className="h-5 w-5 text-gray-400" />
                            </div>
                          </div>

                          {formData.Age !== null && (
                            <p className="text-sm text-gray-600 mt-1">
                              Your Age: {formData.Age} years
                            </p>
                          )}
                          {errors.DOB && (
                            <p className="text-red-500 text-sm mt-1">
                              {errors.DOB}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Address Information */}
                  <div className="mb-8">
                    <h2 className="text-xl font-semibold mb-4 flex items-center">
                      <FaMapMarkerAlt className="mr-2 text-[#ca8032]" />
                      Address Information (optional)
                    </h2>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Address
                        </label>
                        <input
                          type="text"
                          name="Address"
                          value={formData.Address}
                          onChange={handleInputChange}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Country
                        </label>
                        <CountryDropdown
                          value={formData.country}
                          onChange={(val) =>
                            setFormData({ ...formData, country: val })
                          }
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          State/Province
                        </label>
                        <RegionDropdown
                          country={formData.country}
                          value={formData.state}
                          onChange={(val) =>
                            setFormData({ ...formData, state: val })
                          }
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          City
                        </label>
                        <input
                          type="text"
                          name="city"
                          value={formData.city}
                          onChange={handleInputChange}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          ZIP/Postal Code
                        </label>
                        <input
                          type="text"
                          name="zip_code"
                          value={formData.zip_code}
                          onChange={handleInputChange}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Driver's License Country
                        </label>
                        <CountryDropdown
                          value={formData.driver_country}
                          onChange={(val) =>
                            setFormData({ ...formData, driver_country: val })
                          }
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Flight Information */}
                  <div className="mb-8">
                    <h2 className="text-xl font-semibold mb-4 flex items-center">
                      <FaInfoCircle className="mr-2 text-[#ca8032] " />
                      Flight Information (Optional)
                    </h2>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Flight Number
                        </label>
                        <input
                          type="text"
                          name="flight_number"
                          value={formData.flight_number}
                          onChange={handleInputChange}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Airline
                        </label>
                        <input
                          type="text"
                          name="flight"
                          value={formData.flight}
                          onChange={handleInputChange}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Optional Services */}
                  {optionalServices.length > 0 && (
                    <div className="mb-8">
                      <h2 className="text-xl font-semibold mb-4 flex items-center">
                        <FaInfoCircle className="mr-2 text-[#ca8032]" />
                        Optional Services
                      </h2>

                      <div className="flex flex-col gap-3">
                        {optionalServices.map((service) => {
                          const isUnderageService =
                            service.name.toLowerCase().includes("under age") ||
                            service.name.toLowerCase().includes("driver's age");

                          const isUnderage =
                            parseInt(formData.Age, 10) >= 21 &&
                            parseInt(formData.Age, 10) <= 24;

                          return (
                            <div
                              key={service.uid}
                              className="flex items-center justify-between p-4 border rounded-lg"
                            >
                              <div className="flex items-center">
                                <input
                                  type="checkbox"
                                  id={`service-${service.uid}`}
                                  checked={formData.selectedServices.some(
                                    (s) => s.uid === service.uid
                                  )}
                                  onChange={() =>
                                    !isUnderageService || !isUnderage
                                      ? handleServiceSelection(service)
                                      : null
                                  }
                                  disabled={isUnderageService && isUnderage}
                                  className={`h-4 w-4 text-[#ca8032] border-gray-300 rounded ${
                                    isUnderageService && isUnderage
                                      ? "cursor-not-allowed opacity-70"
                                      : "cursor-pointer"
                                  }`}
                                />
                                <label
                                  htmlFor={`service-${service.uid}`}
                                  className="ml-3 text-sm text-gray-700 cursor-pointer"
                                >
                                  {service.name}
                                </label>
                              </div>
                              <span className="text-[#ca8032] font-semibold">
                                {vehicleData?.Currency || "CAD"}{" "}
                                {Number(service.base_rate || 0)}/day
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Policies Section - Dropdown */}
                  {policies.length > 0 && (
                    <div className="bg-white p-3 mb-8 border rounded-lg">
                      <button
                        type="button"
                        onClick={() => setShowPolicies(!showPolicies)}
                        className="w-full flex items-center justify-between text-xl font-semibold mb-2"
                      >
                        <div className="flex items-center">
                          <FaShieldAlt className="mr-2 text-[#ca8032]" />
                          Rental Policies
                        </div>
                        {showPolicies ? (
                          <FaChevronUp className="text-gray-500" />
                        ) : (
                          <FaChevronDown className="text-gray-500" />
                        )}
                      </button>

                      {showPolicies && (
                        <div className="space-y-3">
                          {policies.map((policy, index) => (
                            <div key={index} className="text-sm">
                              <h4 className="font-medium text-gray-700">
                                {policy.name}
                              </h4>
                              <p className="text-gray-600">{policy.content}</p>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Card Details Section - Conditional for POA */}
                  {paymentType === "POA" && (
                    <div className="mb-8">
                      <h2 className="text-xl font-semibold mb-4 flex items-center">
                        <FaInfoCircle className="mr-2 text-[#ca8032]" />
                        Payment Information (Pay on Arrival)
                      </h2>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div className="md:col-span-2">
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Cardholder Name *
                          </label>
                          <input
                            type="text"
                            name="c_name"
                            value={formData.c_name}
                            onChange={handleInputChange}
                            className="w-full px-6 py-4 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                            placeholder="Full name on card"
                            required={paymentType === "POA"}
                          />
                          {errors.c_name && (
                            <p className="text-red-500 text-sm">
                              {errors.c_name}
                            </p>
                          )}
                        </div>

                        {/* --- MODIFIED CARD NUMBER INPUT WITH EYE TOGGLE --- */}
                        <div className="md:col-span-2">
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Card Number *
                          </label>
                          <div className="relative">
                            <input
                              type={showCardNumber ? "text" : "password"} // Toggle functionality
                              inputMode="numeric"
                              name="c_number"
                              value={formData.c_number}
                              onChange={handleInputChange}
                              // Added pr-10 to prevent text overlap with eye icon
                              className="w-full pl-6 pr-10 py-4 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                              placeholder="1234 5678 9012 3456"
                              maxLength="19"
                              required
                            />
                            {/* Eye Icon Button */}
                            <button
                              type="button"
                              onClick={() => setShowCardNumber(!showCardNumber)}
                              className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-500 hover:text-[#ca8032] cursor-pointer"
                            >
                              {showCardNumber ? (
                                <FaEyeSlash size={20} />
                              ) : (
                                <FaEye size={20} />
                              )}
                            </button>
                          </div>
                          {errors.c_number && (
                            <p className="text-red-500 text-sm">
                              {errors.c_number}
                            </p>
                          )}
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Expiry Month *
                          </label>
                          <select
                            name="c_expiry_m"
                            value={formData.c_expiry_m}
                            onChange={handleInputChange}
                            className="w-full px-6 py-4 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                            required
                          >
                            <option value="">Month</option>
                            <option value="01">01 - January</option>
                            <option value="02">02 - February</option>
                            <option value="03">03 - March</option>
                            <option value="04">04 - April</option>
                            <option value="05">05 - May</option>
                            <option value="06">06 - June</option>
                            <option value="07">07 - July</option>
                            <option value="08">08 - August</option>
                            <option value="09">09 - September</option>
                            <option value="10">10 - October</option>
                            <option value="11">11 - November</option>
                            <option value="12">12 - December</option>
                          </select>
                          {errors.c_expiry_m && (
                            <p className="text-red-500 text-sm">
                              {errors.c_expiry_m}
                            </p>
                          )}
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Expiry Year *
                          </label>
                          <select
                            name="c_expiry_y"
                            value={formData.c_expiry_y}
                            onChange={handleInputChange}
                            className="w-full px-6 py-4 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                            required
                          >
                            <option value="">Year</option>
                            {Array.from({ length: 20 }, (_, i) => {
                              const year = new Date().getFullYear() + i;
                              return (
                                <option key={year} value={year.toString()}>
                                  {year}
                                </option>
                              );
                            })}
                          </select>
                          {errors.c_expiry_y && (
                            <p className="text-red-500 text-sm">
                              {errors.c_expiry_y}
                            </p>
                          )}
                        </div>

                        {/* --- MODIFIED CVV INPUT WITH EYE TOGGLE --- */}
                        <div className="md:col-span-2">
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            CVV *
                          </label>
                          <div className="relative">
                            <input
                              type={showCVV ? "text" : "password"} // Toggle functionality
                              inputMode="numeric"
                              name="c_code"
                              value={formData.c_code}
                              onChange={handleInputChange}
                              // Added pr-10 to prevent text overlap with eye icon
                              className="w-full pl-6 pr-10 py-4 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                              placeholder="123"
                              maxLength="4"
                              required
                            />
                            {/* Eye Icon Button */}
                            <button
                              type="button"
                              onClick={() => setShowCVV(!showCVV)}
                              className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-500 hover:text-[#ca8032] cursor-pointer"
                            >
                              {showCVV ? (
                                <FaEyeSlash size={20} />
                              ) : (
                                <FaEye size={20} />
                              )}
                            </button>
                          </div>
                          {errors.c_code && (
                            <p className="text-red-500 text-sm">
                              {errors.c_code}
                            </p>
                          )}
                          <p className="text-xs text-gray-500 mt-1">
                            3-4 digits on the back of your card
                          </p>
                        </div>
                      </div>

                      <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-md">
                        <p className="text-sm text-blue-800">
                          <FaInfoCircle className="inline mr-1" />
                          Your card details are required for verification
                          purposes only. You will pay the full amount when you
                          pick up the vehicle.
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* RIGHT COLUMN (Pricing Summary + Submit section) */}

              <div className="lg:w-1/3">
                <div className="lg:sticky lg:top-24">
                  <div className="bg-white rounded-lg shadow-lg">
                    <div className="bg-[#006039] text-[#edebec] p-4 ">
                      <h2 className="text-xl font-semibold flex items-center">
                        <FaCar className="mr-2 text-[#edebec]" />
                        Pricing Summary
                      </h2>
                    </div>

                    <div className="space-y-1 text-md p-6">
                      {/* Base  Rate */}
                      <div className="flex justify-between">
                        <span className="font-medium text-gray-700">
                          Base Rate:
                        </span>
                        <span className="font-medium text-black">
                          {vehicleData.Currency} {Number(baseRate).toFixed(2)}
                        </span>
                      </div>

                      {/* Taxes */}
                      <div className="border-t pt-2">
                        <div
                          className="flex justify-between items-center cursor-pointer py-2"
                          onClick={() => setShowTaxes(!showTaxes)}
                        >
                          <div className="flex items-center justify-between cursor-pointer mb-2 gap-2">
                            <span className="font-medium text-gray-700 text-md">
                              Total Taxes & Fees
                            </span>
                            {showTaxes ? (
                              <FaChevronUp className="text-gray-700" />
                            ) : (
                              <FaChevronDown className="text-gray-700" />
                            )}
                          </div>

                          <div className="flex items-center space-x-2">
                            <span className="font-semibold">
                              {vehicleData.Currency}{" "}
                              {Number(taxOnCar).toFixed(2)}
                            </span>
                          </div>
                        </div>

                        {showTaxes && (
                          <div className="mt-2 border rounded-md bg-gray-50">
                            {[
                              vehicleData.calculated_tax_charge,
                              vehicleData.independent_tax_charge,
                              vehicleData.baserate_tax_charge,
                            ]
                              .filter(Boolean)
                              .flatMap((taxGroup) => Object.entries(taxGroup))
                              .map(([taxName, taxAmount], idx, arr) => (
                                <div
                                  key={taxName}
                                  className={`flex justify-between items-center px-3 py-2 text-xs text-gray-700 ${
                                    idx < arr.length - 1 ? "border-b" : ""
                                  }`}
                                >
                                  <span>{taxName}</span>
                                  <span className="font-medium">
                                    {vehicleData.Currency}{" "}
                                    {Number(taxAmount).toFixed(2)}
                                  </span>
                                </div>
                              ))}
                          </div>
                        )}
                      </div>

                      {/* Extras */}
                      {Number(formData.Extras_Total_Price || 0) > 0 && (
                        <div>
                          <div
                            className="flex justify-between items-center cursor-pointer py-2"
                            onClick={() => setShowExtras(!showExtras)}
                          >
                            <div className="flex items-center justify-between cursor-pointer mb-2 gap-2">
                              <span className="font-medium text-gray-700 text-sm mb-2">
                                Extras
                              </span>
                              {showExtras ? (
                                <FaChevronUp className="text-gray-700" />
                              ) : (
                                <FaChevronDown className="text-gray-700" />
                              )}
                            </div>
                            <div className="flex items-center space-x-2">
                              <span className="font-semibold">
                                {vehicleData.Currency}{" "}
                                {Number(
                                  formData.Extras_Total_Price || 0
                                ).toFixed(2)}
                              </span>
                            </div>
                          </div>
                          {showExtras && (
                            <div className="mt-2 border rounded-md bg-gray-50 max-h-40 overflow-auto">
                              <ul className="text-xs text-gray-700 p-2">
                                {formData.selectedServices.map((s) => (
                                  <li
                                    key={`sel-dropdown-${s.uid}`}
                                    className="flex justify-between border-b last:border-b-0 py-1"
                                  >
                                    <span>{s.name}</span>
                                    <span>
                                      {vehicleData.Currency}{" "}
                                      {Number(s.total || 0).toFixed(2)}
                                    </span>
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}
                        </div>
                      )}

                      {/* Total */}
                      <div className="flex justify-between font-semibold text-xl pt-2 border-t ">
                        <span className="text-[#006039]">Total:</span>
                        <span className="text-[#ca8032] font-bold text-2xl">
                          {vehicleData.Currency}{" "}
                          {Number(formData.Total_Price || 0).toFixed(2)}
                        </span>
                      </div>

                      {/* Terms & Conditions */}
                      <div className="mt-6">
                        <div className="flex items-center">
                          <input
                            type="checkbox"
                            id="accept_terms"
                            name="accept_terms"
                            checked={formData.accept_terms}
                            onChange={handleInputChange}
                            className="h-4 w-4 text-orange-600 focus:ring-orange-500 border-gray-300 rounded"
                            required
                          />
                          <label
                            htmlFor="accept_terms"
                            className="ml-2 text-sm text-gray-700 mt-2 mb-4"
                          >
                            I accept the terms and conditions *
                          </label>
                          {errors.accept_terms && (
                            <p className="text-red-500 text-sm">
                              {errors.accept_terms}
                            </p>
                          )}
                        </div>
                      </div>

                      {/* Captcha */}
                      <div className="mt-6">
                        <label className="block text-sm text-gray-700 mb-1">
                          Enter the text from the image *
                        </label>
                        <div className="flex items-center gap-4 mb-2">
                          <div
                            className="bg-gray-100 px-4 py-2 rounded select-none font-mono text-lg tracking-widest"
                            style={{ userSelect: "none" }}
                          >
                            {captchaText}
                          </div>
                          <button
                            type="button"
                            onClick={generateCaptcha}
                            className="text-gray-600 hover:text-gray-900"
                            aria-label="Refresh CAPTCHA"
                          >
                            &#x21bb;
                          </button>
                        </div>
                        <input
                          type="text"
                          value={userCaptchaInput}
                          onChange={(e) => {
                            setUserCaptchaInput(e.target.value);
                            setErrors((prev) => ({ ...prev, captcha: "" }));
                          }}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                          maxLength={6}
                          placeholder="Enter CAPTCHA"
                          required
                        />
                        {errors.captcha && (
                          <p className="text-red-500 text-sm">
                            {errors.captcha}
                          </p>
                        )}
                      </div>

                      {/* Submit Button */}
                      <div className="flex justify-center mt-6">
                        <button
                          type="submit"
                          disabled={submitting}
                          className="bg-[#ca8032] text-white px-12 py-4 rounded-lg text-lg font-semibold hover:bg-[#a06427] disabled:opacity-50 disabled:cursor-not-allowed w-full"
                        >
                          {submitting ? (
                            <>
                              <FaSpinner className="animate-spin inline mr-2" />
                              Processing...
                            </>
                          ) : (
                            "Complete Booking"
                          )}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </form>
        </div>
      </div>
    </>
  );
}

export default Checkout;